import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaCalendar, FaChevronLeft, FaChevronRight, FaList, FaInfoCircle } from 'react-icons/fa';
import { formatDateInTimezone } from '../../utils/dateUtils';

const EventsSection = ({
  events = [],
  eventsLoading = false,
  onEventClick
}) => {
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  const [showAllEventsModal, setShowAllEventsModal] = useState(false);

  const getSortedEvents = () => {
    if (!events || !Array.isArray(events)) return [];
    const activeEvents = events.filter(event => event.isActive !== false);
    return [...activeEvents].sort((a, b) => {
      if (a.order && b.order) return a.order - b.order;
      if (a.order && !b.order) return -1;
      if (!a.order && b.order) return 1;
      return 0;
    });
  };

  const sortedEvents = getSortedEvents();
  const total = sortedEvents.length;

  // Clamp activeEventIndex when events change
  useEffect(() => {
    if (total > 0 && activeEventIndex >= total) {
      setActiveEventIndex(total - 1);
    }
  }, [total]);

  // Desktop: window of 3 centred on active event
  const getVisibleWindow = () => {
    if (total === 0) return [];
    if (total <= 3) return sortedEvents;
    let start = Math.max(0, activeEventIndex - 1);
    let end = start + 3;
    if (end > total) { end = total; start = Math.max(0, end - 3); }
    return sortedEvents.slice(start, end);
  };

  const visibleEvents = getVisibleWindow();
  const canGoPrevious = activeEventIndex > 0;
  const canGoNext = activeEventIndex < total - 1;

  const goToPrevious = () => { if (canGoPrevious) setActiveEventIndex(i => i - 1); };
  const goToNext    = () => { if (canGoNext)     setActiveEventIndex(i => i + 1); };

  const handleEventClick = (event) => { if (onEventClick) onEventClick(event); };

  // ── Shared sub-components ────────────────────────────────────────────
  const TypeBadge = ({ event, size = 'sm' }) => {
    if (event.type === 'event' && event.date) {
      return (
        <div className={`bg-primary text-primary-foreground rounded-xl font-semibold shadow text-center leading-tight ${size === 'sm' ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-xs'}`}>
          <div>{formatDateInTimezone(event.date, event.timezone || 'UTC', { day: '2-digit', month: 'short' })}</div>
          <div>{formatDateInTimezone(event.date, event.timezone || 'UTC', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
        </div>
      );
    }
    if (event.type === 'information') {
      return (
        <div className={`bg-violet-500 text-white rounded-xl font-semibold shadow flex items-center gap-1 ${size === 'sm' ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-xs'}`}>
          <FaInfoCircle className="w-2.5 h-2.5 flex-shrink-0" />
          Info
        </div>
      );
    }
    return null;
  };

  const EventThumbnail = ({ event, className = '' }) => (
    <div className={`relative bg-muted overflow-hidden ${className}`}>
      {event.thumbnail?.url || event.thumbnail?.data ? (
        <img
          src={event.thumbnail.url || `data:${event.thumbnail.contentType};base64,${event.thumbnail.data}`}
          alt={event.title}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
          <FaCalendar className="w-8 h-8 text-primary/60" />
        </div>
      )}
    </div>
  );

  const activeEvent = sortedEvents[activeEventIndex];

  return (
    <>
      <div className="bg-white px-4 sm:px-6 py-6 sm:py-8">

        {/* ── Mobile Header ────────────────────────────────────────── */}
        <div className="block sm:hidden mb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground">What's Happening</h2>
            <button
              onClick={() => setShowAllEventsModal(true)}
              className="w-8 h-8 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <FaList className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* ── Mobile: horizontal scroll, 2 cards visible ───────────── */}
        <div className="block sm:hidden">
          {eventsLoading ? (
            <div className="flex gap-3 overflow-hidden">
              {[0,1].map(i => (
                <div key={i} className="flex-shrink-0 w-[calc(50%-6px)] rounded-2xl border border-border overflow-hidden animate-pulse">
                  <div className="aspect-[3/4] bg-muted" />
                  <div className="p-3 flex flex-col gap-2">
                    <div className="h-3 bg-muted-foreground/10 rounded w-3/4" />
                    <div className="h-3 bg-muted-foreground/10 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : !total ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FaCalendar className="w-6 h-6 text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">No events</p>
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-hide scroll-smooth">
              <div className="flex gap-3 pb-2">
                {sortedEvents.map((event) => (
                  <div
                    key={event._id}
                    onClick={() => handleEventClick(event)}
                    className="flex-shrink-0 w-[calc(50%-6px)] rounded-2xl border border-border overflow-hidden cursor-pointer flex flex-col shadow-soft hover:border-primary/40 hover:shadow-elevated transition-colors duration-200"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden flex-shrink-0">
                      <EventThumbnail event={event} className="w-full h-full" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      <div className="absolute top-2 right-2">
                        <TypeBadge event={event} size="sm" />
                      </div>
                    </div>
                    <div className="px-3 py-2.5 bg-card">
                      <p className="text-foreground text-xs font-semibold leading-tight line-clamp-2">{event.title}</p>
                      {event.description && (
                        <div
                          className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5"
                          dangerouslySetInnerHTML={{ __html: event.description }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Desktop Header ───────────────────────────────────────── */}
        <div className="hidden sm:flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">What's Happening</h2>
          <div className="flex items-center gap-2">
            {total > 3 && (
              <>
                <button
                  onClick={goToPrevious}
                  disabled={!canGoPrevious}
                  className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-colors ${canGoPrevious ? 'border-border text-foreground hover:border-primary hover:text-primary cursor-pointer' : 'border-border text-muted-foreground cursor-not-allowed opacity-40'}`}
                >
                  <FaChevronLeft className="w-3 h-3" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={!canGoNext}
                  className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-colors ${canGoNext ? 'border-border text-foreground hover:border-primary hover:text-primary cursor-pointer' : 'border-border text-muted-foreground cursor-not-allowed opacity-40'}`}
                >
                  <FaChevronRight className="w-3 h-3" />
                </button>
              </>
            )}
            <button
              onClick={() => setShowAllEventsModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
            >
              View All ({total})
            </button>
          </div>
        </div>

        {/* ── Desktop: 3-column grid carousel ─────────────────────── */}
        <div className="hidden sm:block">
          {eventsLoading ? (
            <div className="grid grid-cols-3 gap-4">
              {[0,1,2].map(i => (
                <div key={i} className="rounded-2xl border border-border overflow-hidden animate-pulse">
                  <div className="aspect-[3/4] bg-muted" />
                  <div className="p-4 flex flex-col gap-2">
                    <div className="h-4 bg-muted rounded-full w-3/4" />
                    <div className="h-3 bg-muted rounded-full w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : !total ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-3">
                <FaCalendar className="w-7 h-7 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">No upcoming events</p>
              <p className="text-xs text-muted-foreground mt-1">Check back later for announcements and events.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {visibleEvents.map((event) => {
                const isActive = sortedEvents[activeEventIndex]?._id === event._id;
                return (
                  <div
                    key={event._id}
                    onClick={() => handleEventClick(event)}
                    className={`rounded-2xl border overflow-hidden cursor-pointer flex flex-col transition-colors duration-200 ${
                      isActive
                        ? 'border-primary shadow-elevated'
                        : 'border-border shadow-soft hover:border-primary/40 hover:shadow-elevated'
                    }`}
                  >
                    <div className="relative aspect-[3/4] overflow-hidden flex-shrink-0">
                      <EventThumbnail event={event} className="w-full h-full" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      <div className="absolute top-3 right-3">
                        <TypeBadge event={event} size="sm" />
                      </div>
                    </div>
                    <div className="p-4 flex flex-col gap-1 bg-card flex-1">
                      <h4 className="font-bold text-foreground text-sm leading-tight line-clamp-2">
                        {event.title}
                      </h4>
                      {event.description && (
                        <div
                          className="text-xs text-muted-foreground line-clamp-2 mt-0.5"
                          dangerouslySetInnerHTML={{ __html: event.description }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Desktop dot indicators */}
          {total > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {sortedEvents.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveEventIndex(i)}
                  className={`rounded-full transition-all duration-200 ${
                    i === activeEventIndex ? 'w-4 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/60'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── View All Modal ────────────────────────────────────────────── */}
      {showAllEventsModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAllEventsModal(false); }}
        >
          <div className="bg-background rounded-2xl shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh] border border-border">

            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-foreground">All Events</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{total} event{total !== 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={() => setShowAllEventsModal(false)}
                className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground transition-colors text-lg"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
              {eventsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[0,1,2,3,4,5].map(i => (
                    <div key={i} className="rounded-2xl border border-border overflow-hidden animate-pulse">
                      <div className="aspect-[4/3] bg-muted" />
                      <div className="p-4 flex flex-col gap-2">
                        <div className="h-4 bg-muted rounded-full w-3/4" />
                        <div className="h-3 bg-muted rounded-full w-full" />
                        <div className="h-3 bg-muted rounded-full w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : !total ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-3">
                    <FaCalendar className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground">No upcoming events</p>
                  <p className="text-xs text-muted-foreground mt-1">Check back later for new announcements.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sortedEvents.map((event) => (
                    <div
                      key={event._id}
                      onClick={() => { handleEventClick(event); setShowAllEventsModal(false); }}
                      className="rounded-2xl border border-border overflow-hidden cursor-pointer group hover:border-primary hover:shadow-elevated transition-all duration-200 flex flex-col bg-card"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden flex-shrink-0">
                        <EventThumbnail event={event} className="w-full h-full group-hover:scale-105 transition-transform duration-300" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                        <div className="absolute top-3 right-3">
                          <TypeBadge event={event} size="md" />
                        </div>
                      </div>
                      <div className="p-4 flex flex-col flex-1 gap-2">
                        <h4 className="font-bold text-foreground text-sm leading-snug line-clamp-2">{event.title}</h4>
                        {event.description && (
                          <div
                            className="text-xs text-muted-foreground line-clamp-3 flex-1"
                            dangerouslySetInnerHTML={{ __html: event.description }}
                          />
                        )}
                        {event.type === 'event' && event.date && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-auto pt-2 border-t border-border">
                            <FaCalendar className="w-3 h-3 text-primary flex-shrink-0" />
                            <span>
                              {formatDateInTimezone(event.date, event.timezone || 'UTC', {
                                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit', hour12: true
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default EventsSection;
